import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, DatePicker, Empty, Row, Select, Space, Table, Typography, message, Checkbox, Tooltip } from 'antd';
import { EnvironmentOutlined, ArrowLeftOutlined, CheckOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate, useLocation } from 'react-router-dom';
import AreaMap from '../components/Map/AreaMap';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import '../style/Measurements.css';
import { formatMeasurementTimestamp, formatUtcTimestamp } from '../utils/measurementTime';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

interface Area {
    id: number;
    name: string;
    code?: string;
    areaType?: { id: number; name: string } | null;
    parentAreaId?: number | null;
    geom?: any;
}

interface Measurement {
    id: number;
    measurementCode: string;
    createdAt: string;
    measuredAt?: string | null;
    measuredTimezone?: string | null;
    measuredUtcOffsetMinutes?: number | null;
    timeSource?: string | null;
    timezoneSource?: string | null;
    areaId?: number | null;
    area?: { id: number; name: string } | null;
    device?: { id: number; label: string } | null;
    geom?: { type: string; coordinates: number[] } | null;
}

const MeasurementAssignAreas: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [areas, setAreas] = useState<Area[]>([]);
    const [measurements, setMeasurements] = useState<Measurement[]>([]);
    const [selectedArea, setSelectedArea] = useState<Area | null>(null);
    const [selectedMeasurementIds, setSelectedMeasurementIds] = useState<number[]>([]);
    const [focusedMeasurement, setFocusedMeasurement] = useState<Measurement | null>(null);
    const [loading, setLoading] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [areaTypeTemplates, setAreaTypeTemplates] = useState<Map<number, string>>(new Map());
    const [checkedAreaTypes, setCheckedAreaTypes] = useState<Set<number>>(new Set());
    
    // Load date range from localStorage or use default
    const getInitialDateRange = (): [Dayjs, Dayjs] => {
        try {
            const stored = localStorage.getItem('measurementDateRange');
            if (stored) {
                const { from, to } = JSON.parse(stored);
                return [dayjs(from), dayjs(to)];
            }
        } catch (e) {
            console.warn('Failed to load date range from localStorage', e);
        }
        return [dayjs().subtract(7, 'day'), dayjs()];
    };
    
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(getInitialDateRange());

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params: Record<string, any> = { limit: 1000 };
            
            // Apply date range filter
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.from_date = dateRange[0].startOf('day').toISOString();
                params.to_date = dateRange[1].endOf('day').toISOString();
            }
            
            const [areasRes, measurementsRes] = await Promise.all([
                axios.get(ENDPOINTS.AREAS_V2, { withCredentials: true }),
                axios.get(ENDPOINTS.MEASUREMENTS_V2, { 
                    params,
                    withCredentials: true 
                }),
            ]);

            setAreas(areasRes.data?.data || areasRes.data || []);
            
            // Filter measurements without area assignment
            const allMeasurements = measurementsRes.data?.data || measurementsRes.data || [];
            const unassigned = allMeasurements.filter((m: Measurement) => !m.areaId);
            console.log('[MeasurementAssignAreas] Filtering measurements:', {
                total: allMeasurements.length,
                unassigned: unassigned.length,
                dateRange: {
                    from: params.from_date,
                    to: params.to_date
                }
            });
            setMeasurements(unassigned);
        } catch (error) {
            console.error('Failed to load data', error);
            message.error('Failed to load areas or measurements');
        } finally {
            setLoading(false);
        }
    }, [dateRange]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Pre-select measurement from URL parameter
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const measurementIdParam = searchParams.get('measurementId');
        
        if (measurementIdParam && measurements.length > 0) {
            const measurementId = parseInt(measurementIdParam, 10);
            const measurement = measurements.find(m => m.id === measurementId);
            
            if (measurement) {
                // Select and focus on this measurement
                setSelectedMeasurementIds([measurementId]);
                setFocusedMeasurement(measurement);
            }
        }
    }, [location.search, measurements]);

    // Load area type template for selected area
    useEffect(() => {
        const loadTemplate = async () => {
            if (!selectedArea?.areaType?.id) return;
            const typeId = selectedArea.areaType.id;
            if (checkedAreaTypes.has(typeId)) return;
            
            setCheckedAreaTypes(prev => new Set(prev).add(typeId));
            
            try {
                const res = await axios.get(`${ENDPOINTS.AREA_TYPES_V2}/${typeId}`, {
                    withCredentials: true,
                });
                const hasTemplate = res.data?.data?.templateImage || res.data?.templateImage;
                
                if (hasTemplate) {
                    const imageUrl = `${ENDPOINTS.AREA_TYPES_V2}/${typeId}/template-image?t=${Date.now()}`;
                    setAreaTypeTemplates(prev => {
                        const next = new Map(prev);
                        next.set(typeId, imageUrl);
                        return next;
                    });
                }
            } catch (err) {
                console.warn('Failed to load area type template', err);
            }
        };
        loadTemplate();
    }, [selectedArea, checkedAreaTypes]);

    const measurementMarkers = useMemo(() => {
        return measurements
            .filter(m => m.geom?.type === 'Point' && Array.isArray(m.geom.coordinates))
            .map(m => ({
                id: m.id,
                lat: m.geom!.coordinates[1],
                lng: m.geom!.coordinates[0],
                label: m.measurementCode,
                selected: selectedMeasurementIds.includes(m.id),
            }));
    }, [measurements, selectedMeasurementIds]);

    // Handle measurement click on map - toggle selection
    const handleMeasurementClick = useCallback((measurementId: string | number) => {
        const id = typeof measurementId === 'string' ? parseInt(measurementId, 10) : measurementId;
        
        setSelectedMeasurementIds(prev => {
            if (prev.includes(id)) {
                // Deselect if already selected
                return prev.filter(mId => mId !== id);
            } else {
                // Select if not already selected
                return [...prev, id];
            }
        });
    }, []);

    const mapInitialView = useMemo(() => {
        if (focusedMeasurement?.geom?.type === 'Point' && Array.isArray(focusedMeasurement.geom.coordinates)) {
            return {
                longitude: focusedMeasurement.geom.coordinates[0],
                latitude: focusedMeasurement.geom.coordinates[1],
                zoom: 17,
            };
        }
        return undefined;
    }, [focusedMeasurement]);

    // Build hierarchy path for an area
    const buildHierarchyPath = useCallback((area: Area, allAreas: Area[]): string => {
        const path: string[] = [];
        let current: Area | undefined = area;
        let depth = 0;
        const maxDepth = 10; // Prevent infinite loops

        while (current && depth < maxDepth) {
            path.unshift(current.name);
            if (current.parentAreaId) {
                current = allAreas.find(a => a.id === current!.parentAreaId);
            } else {
                break;
            }
            depth++;
        }

        return path.join(' > ');
    }, []);

    // Create area options with hierarchy
    const areaOptions = useMemo(() => {
        return areas.map(a => {
            const hierarchyPath = buildHierarchyPath(a, areas);
            const typeInfo = a.areaType?.name ? ` (${a.areaType.name})` : '';
            return {
                value: a.id,
                label: `${hierarchyPath}${typeInfo}`,
            };
        });
    }, [areas, buildHierarchyPath]);

    const handleAssign = async () => {
        if (selectedMeasurementIds.length === 0) {
            message.warning('Please select measurements to assign');
            return;
        }

        if (!selectedArea) {
            message.warning('Please select a target area');
            return;
        }

        setAssigning(true);
        try {
            const res = await axios.patch(
                ENDPOINTS.MEASUREMENTS_BULK_UPDATE_AREA,
                {
                    measurementIds: selectedMeasurementIds,
                    areaId: selectedArea.id,
                },
                { withCredentials: true }
            );

            message.success(res.data.message || `${res.data.data.updatedCount} measurements assigned successfully`);
            
            // Refresh data and clear selection
            setSelectedMeasurementIds([]);
            fetchData();
        } catch (error: any) {
            console.error('Failed to assign measurements', error);
            message.error(error.response?.data?.message || 'Failed to assign measurements');
        } finally {
            setAssigning(false);
        }
    };

    const toggleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedMeasurementIds(measurements.map(m => m.id));
        } else {
            setSelectedMeasurementIds([]);
        }
    };

    const columns: ColumnsType<Measurement> = [
        {
            title: (
                <Checkbox
                    checked={selectedMeasurementIds.length === measurements.length && measurements.length > 0}
                    indeterminate={selectedMeasurementIds.length > 0 && selectedMeasurementIds.length < measurements.length}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                />
            ),
            key: 'select',
            width: 50,
            render: (_, record) => (
                <Checkbox
                    checked={selectedMeasurementIds.includes(record.id)}
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelectedMeasurementIds([...selectedMeasurementIds, record.id]);
                        } else {
                            setSelectedMeasurementIds(selectedMeasurementIds.filter(id => id !== record.id));
                        }
                    }}
                />
            ),
        },
        {
            title: 'Code',
            dataIndex: 'measurementCode',
            key: 'measurementCode',
        },
        {
            title: 'Date',
            key: 'date',
            render: (_, record) => {
                if (record.measuredAt) {
                    return formatMeasurementTimestamp(record.measuredAt, record);
                }
                return (
                    <Tooltip title="Using creation date - measured date not available">
                        <span>
                            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 4 }} />
                            {formatUtcTimestamp(record.createdAt)}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Device',
            dataIndex: ['device', 'label'],
            key: 'device',
            render: (_, record) => record.device?.label || '—',
        },
        {
            title: 'GPS',
            key: 'gps',
            render: (_, record) => (
                record.geom?.type === 'Point' ? (
                    <CheckOutlined style={{ color: '#52c41a' }} />
                ) : (
                    <Text type="secondary">—</Text>
                )
            ),
        },
    ];

    return (
        <div className="measurements-page measurement-assign-areas-page">
            <Row justify="start" align="middle" className="measurements-header">
                <Col>
                    <Space size="middle" align="center">
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/measurements')}>
                            Back
                        </Button>
                        <Title level={3} style={{ margin: 0 }}>Assign measurements to areas</Title>
                    </Space>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} xl={15}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Card size="small" className="assign-areas-date-filter-card" style={{ background: '#f5f5f5' }}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Date range filter</Text>
                                <RangePicker
                                    className="assign-areas-date-range"
                                    value={dateRange}
                                    onChange={(range) => {
                                        if (range) {
                                            const newRange = range as [Dayjs, Dayjs];
                                            setDateRange(newRange);
                                            
                                            // Persist to localStorage
                                            localStorage.setItem('measurementDateRange', JSON.stringify({
                                                from: newRange[0].format('YYYY-MM-DD'),
                                                to: newRange[1].format('YYYY-MM-DD')
                                            }));
                                        }
                                    }}
                                    allowClear={false}
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Filters measurements by measured date
                                </Text>
                            </Space>
                        </Card>
                        <Card size="small" className="assign-areas-target-card" style={{ background: '#fff3e0' }}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Select target area</Text>
                                <Select
                                    className="assign-areas-target-select"
                                    showSearch
                                    placeholder="Choose area to assign measurements to"
                                    value={selectedArea?.id}
                                    onChange={(areaId) => {
                                        const area = areas.find(a => a.id === areaId);
                                        setSelectedArea(area || null);
                                    }}
                                    style={{ width: '100%' }}
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={areaOptions}
                                />
                                {selectedArea && (
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        <EnvironmentOutlined /> Selected: {selectedArea.name}
                                    </Text>
                                )}
                            </Space>
                        </Card>

                        <Card title="Unassigned measurements" className="assign-areas-table-card">
                            {loading ? (
                                <Text>Loading...</Text>
                            ) : measurements.length === 0 ? (
                                <Empty description="All measurements are assigned to areas" />
                            ) : (
                                <Table
                                    size="small"
                                    rowKey="id"
                                    columns={columns}
                                    dataSource={measurements}
                                    pagination={{
                                        pageSize: 20,
                                        showTotal: (total) => `${total} unassigned measurements`,
                                    }}
                                    rowClassName={(record) =>
                                        selectedMeasurementIds.includes(record.id) ? 'area-row-selected' : ''
                                    }
                                    onRow={(record) => ({
                                        onClick: () => setFocusedMeasurement(record),
                                        style: { cursor: 'pointer' }
                                    })}
                                />
                            )}
                        </Card>
                    </Space>
                </Col>

                <Col xs={24} xl={9}>
                    <Card title="Map preview" className="measurements-map-card assign-areas-map-card">
                        <div className="measurements-map-wrapper">
                            <AreaMap
                                mapKey={`assign-${selectedArea?.id || 'none'}-${focusedMeasurement?.id || 'none'}`}
                                areaGeometry={selectedArea?.geom}
                                areaName={selectedArea?.name}
                                measurements={measurementMarkers}
                                allAreas={selectedArea ? [selectedArea as any] : []}
                                areaType={selectedArea?.areaType || null}
                                areaTypeTemplates={areaTypeTemplates}
                                initialViewState={mapInitialView}
                                disableAutoFit={!!focusedMeasurement}
                                onMeasurementClick={handleMeasurementClick}
                            />
                        </div>
                        {selectedArea && selectedMeasurementIds.length > 0 && (
                            <div className="assign-areas-preview" style={{ marginTop: 12, padding: '12px', background: '#f0f2f5', borderRadius: '8px' }}>
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Text strong>Assignment preview</Text>
                                    <Text>
                                        {selectedMeasurementIds.length} measurement(s) will be assigned to: <strong>{selectedArea.name}</strong>
                                    </Text>
                                    {selectedArea.areaType && (
                                        <Text type="secondary">
                                            Type: {selectedArea.areaType.name}
                                        </Text>
                                    )}
                                </Space>
                            </div>
                        )}

                        <div className="assign-areas-map-actions">
                            <Space direction="vertical" size={10} style={{ width: '100%' }}>
                                <Text strong className="assign-areas-selected-count">
                                    {selectedMeasurementIds.length} selected
                                </Text>
                                <Button
                                    className="assign-areas-assign-button"
                                    type="primary"
                                    onClick={handleAssign}
                                    loading={assigning}
                                    disabled={selectedMeasurementIds.length === 0 || !selectedArea}
                                    block
                                >
                                    Assign to area
                                </Button>
                            </Space>
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default MeasurementAssignAreas;
